import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  Label,
  Badge,
  Spinner,
} from "reactstrap";

import CountUp from "react-countup";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import api from "../../../utills/api";

const RestrictionsManagement = () => {
  document.title = "HMS | Restrictions Management";

  const [roomTypes, setRoomTypes] = useState([]);
  const [restrictionData, setRestrictionData] = useState([]);
  const [restrictionLogs, setRestrictionLogs] = useState([]);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  /* =========================================
     DEFAULT DATES
  ========================================= */

  const getDateString = (date) => {
    return date.toISOString().split("T")[0];
  };

  const getDefaultDates = () => {
    const today = new Date();

    const startDate = getDateString(today);

    const end = new Date(today);
    end.setDate(end.getDate() + 6);

    const endDate = getDateString(end);

    return {
      startDate,
      endDate,
    };
  };

  const defaultDates = getDefaultDates();

  /* =========================================
     FORM
  ========================================= */

  const [formData, setFormData] = useState({
    roomTypeId: "",
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate,
    restrictionType: "STOP_SELL",
    value: "true",
  });

  /* =========================================
     LOAD ROOM TYPES
  ========================================= */

  const loadRoomTypes = useCallback(async () => {
    try {
      const res = await api.get("/manage/room-types");

      setRoomTypes(res.data || []);
    } catch (err) {
      console.error("Room Types Error:", err);
    }
  }, []);

  /* =========================================
     LOAD RESTRICTIONS
  ========================================= */

  const loadRestrictions = useCallback(async () => {
    try {
      const res = await api.get("/restrictions");

      console.log("Restrictions API:", res.data);

      const data = Array.isArray(res.data)
        ? res.data
        : res.data?.data || [];

      setRestrictionData(data);
    } catch (err) {
      console.error("Restrictions Error:", err);
      setRestrictionData([]);
    }
  }, []);

  /* =========================================
     LOAD LOGS
  ========================================= */

  const loadLogs = useCallback(async () => {
    try {
      const res = await api.get("/restrictions/logs");

      setRestrictionLogs(res.data?.logs || []);
    } catch (err) {
      console.error("Restriction Logs Error:", err);
      setRestrictionLogs([]);
    }
  }, []);

  /* =========================================
     INITIAL LOAD
  ========================================= */

  useEffect(() => {
    const loadInitialData = async () => {
      setPageLoading(true);

      await Promise.all([
        loadRoomTypes(),
        loadRestrictions(),
        loadLogs(),
      ]);

      setPageLoading(false);
    };

    loadInitialData();
  }, [
    loadRoomTypes,
    loadRestrictions,
    loadLogs,
  ]);

  /* =========================================
     HANDLE FORM CHANGE
  ========================================= */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =========================================
     HANDLE RESTRICTION TYPE CHANGE
  ========================================= */

  const handleRestrictionTypeChange = (e) => {
    const restrictionType = e.target.value;

    let defaultValue = "true";

    if (
      ["MIN_STAY", "MAX_STAY"].includes(
        restrictionType
      )
    ) {
      defaultValue = "1";
    }

    setFormData((prev) => ({
      ...prev,
      restrictionType,
      value: defaultValue,
    }));
  };

  /* =========================================
     SAVE RESTRICTION
  ========================================= */

  const handleSave = async () => {
    if (!formData.roomTypeId) {
      alert("Please select room type");
      return;
    }

    if (!formData.startDate) {
      alert("Please select start date");
      return;
    }

    if (!formData.endDate) {
      alert("Please select end date");
      return;
    }

    if (formData.startDate > formData.endDate) {
      alert("End date cannot be before start date");
      return;
    }

    if (
      formData.value === "" ||
      formData.value === null ||
      formData.value === undefined
    ) {
      alert("Please enter value");
      return;
    }

    if (
      ["MIN_STAY", "MAX_STAY"].includes(
        formData.restrictionType
      )
    ) {
      const numericValue = Number(formData.value);

      if (
        !Number.isFinite(numericValue) ||
        numericValue < 1
      ) {
        alert("Stay value must be at least 1 night");
        return;
      }
    }

    try {
      setLoading(true);

      let finalValue = formData.value;

      if (
        ["STOP_SELL", "CTA", "CTD"].includes(
          formData.restrictionType
        )
      ) {
        finalValue = formData.value === "true";
      } else {
        finalValue = Number(formData.value);
      }

      const payload = {
        roomTypeId: formData.roomTypeId,
        startDate: formData.startDate,
        endDate: formData.endDate,
        restrictionType: formData.restrictionType,
        value: finalValue,
      };

      console.log(
        "Restriction Payload:",
        payload
      );

      await api.post(
        "/restrictions",
        payload
      );

      await Promise.all([
        loadRestrictions(),
        loadLogs(),
      ]);

      alert(
        "Restriction updated successfully"
      );

      const dates = getDefaultDates();

      setFormData({
        roomTypeId: "",
        startDate: dates.startDate,
        endDate: dates.endDate,
        restrictionType: "STOP_SELL",
        value: "true",
      });
    } catch (err) {
      console.error(
        "Restriction Save Error:",
        err
      );

      alert(
        err.response?.data?.message ||
          "Failed to save restriction"
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================
     WIDGETS
  ========================================= */

  const widgets = useMemo(() => {
    return [
      {
        label: "Active Restrictions",
        value: restrictionData.length,
        icon: "bx bx-lock-alt",
        color: "#dc3545",
        bgColor: "rgba(220,53,69,.12)",
      },

      {
        label: "Stop Sell Rules",
        value: restrictionData.filter(
          (x) => x.stopSell === true
        ).length,
        icon: "bx bx-block",
        color: "#fd7e14",
        bgColor: "rgba(253,126,20,.12)",
      },

      {
        label: "Min Stay Rules",
        value: restrictionData.filter(
          (x) => Number(x.minStay) > 0
        ).length,
        icon: "bx bx-calendar",
        color: "#0d6efd",
        bgColor: "rgba(13,110,253,.12)",
      },

      {
        label: "Max Stay Rules",
        value: restrictionData.filter(
          (x) => Number(x.maxStay) > 0
        ).length,
        icon: "bx bx-calendar-x",
        color: "#198754",
        bgColor: "rgba(25,135,84,.12)",
      },
    ];
  }, [restrictionData]);

  /* =========================================
     FORMAT DATE
  ========================================= */

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  };

  /* =========================================
     RESTRICTION TABLE
  ========================================= */

  const restrictionColumns = useMemo(
    () => [
      {
        header: "Date",
        accessorKey: "date",
        cell: ({ getValue }) =>
          formatDate(getValue()),
      },

      {
        header: "Room Type",
        accessorKey: "roomTypeName",
      },

      {
        header: "Stop Sell",
        accessorKey: "stopSell",
        cell: ({ getValue }) => (
          <Badge
            color={
              getValue()
                ? "danger"
                : "success"
            }
          >
            {getValue() ? "Yes" : "No"}
          </Badge>
        ),
      },

      {
        header: "CTA",
        accessorKey: "cta",
        cell: ({ getValue }) => (
          <Badge
            color={
              getValue()
                ? "warning"
                : "success"
            }
          >
            {getValue() ? "Yes" : "No"}
          </Badge>
        ),
      },

      {
        header: "CTD",
        accessorKey: "ctd",
        cell: ({ getValue }) => (
          <Badge
            color={
              getValue()
                ? "warning"
                : "success"
            }
          >
            {getValue() ? "Yes" : "No"}
          </Badge>
        ),
      },

      {
        header: "Min Stay",
        accessorKey: "minStay",
        cell: ({ getValue }) =>
          Number(getValue() || 0) > 0
            ? `${getValue()} Night${
                Number(getValue()) > 1
                  ? "s"
                  : ""
              }`
            : "-",
      },

      {
        header: "Max Stay",
        accessorKey: "maxStay",
        cell: ({ getValue }) =>
          Number(getValue() || 0) > 0
            ? `${getValue()} Night${
                Number(getValue()) > 1
                  ? "s"
                  : ""
              }`
            : "-",
      },
    ],
    []
  );

  /* =========================================
     LOG TABLE
  ========================================= */

  const logColumns = useMemo(
    () => [
      {
        header: "Date",
        accessorKey: "date",
        cell: ({ getValue }) =>
          formatDate(getValue()),
      },

      {
        header: "Room Type",
        accessorKey: "roomTypeName",
      },

      {
        header: "Restriction",
        accessorKey: "restrictionType",
        cell: ({ getValue }) => {
          const value = getValue();

          const labels = {
            STOP_SELL: "Stop Sell",
            CTA: "CTA",
            CTD: "CTD",
            MIN_STAY: "Min Stay",
            MAX_STAY: "Max Stay",
          };

          return (
            <Badge color="primary">
              {labels[value] || value}
            </Badge>
          );
        },
      },

      {
        header: "Old Value",
        accessorKey: "oldValue",
        cell: ({ getValue }) => {
          const value = getValue();

          if (
            value === undefined ||
            value === null ||
            value === ""
          ) {
            return "-";
          }

          return value === "true"
            ? "Enabled"
            : value === "false"
            ? "Disabled"
            : value;
        },
      },

      {
        header: "New Value",
        accessorKey: "newValue",
        cell: ({ getValue }) => {
          const value = getValue();

          if (
            value === undefined ||
            value === null ||
            value === ""
          ) {
            return "-";
          }

          return value === "true"
            ? "Enabled"
            : value === "false"
            ? "Disabled"
            : value;
        },
      },

      {
        header: "Updated By",
        accessorKey: "updatedBy",
        cell: ({ getValue }) =>
          getValue() || "Admin",
      },
    ],
    []
  );

  /* =========================================
     RENDER
  ========================================= */

  return (
    <div className="page-content">
      <Container fluid>

        <BreadCrumb
          title="Channel Manager"
          pageTitle="Restrictions Management"
        />

        {/* =========================================
            WIDGETS
        ========================================= */}

        <Row>
          {widgets.map(
            (item, index) => (
              <Col
                xl={3}
                md={6}
                key={index}
              >
                <Card>
                  <CardBody className="d-flex justify-content-between align-items-center">

                    <div>
                      <p className="text-uppercase text-muted fw-semibold mb-1">
                        {item.label}
                      </p>

                      <h3 className="mb-0">
                        <CountUp
                          start={0}
                          end={item.value}
                          duration={1.5}
                        />
                      </h3>
                    </div>

                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "10px",
                        backgroundColor:
                          item.bgColor,
                      }}
                    >
                      <i
                        className={item.icon}
                        style={{
                          fontSize: "24px",
                          color: item.color,
                        }}
                      />
                    </div>

                  </CardBody>
                </Card>
              </Col>
            )
          )}
        </Row>

        {/* =========================================
            ACTIVE RESTRICTIONS
        ========================================= */}

        <Row>
          <Col lg={12}>
            <Card>

              <CardHeader className="d-flex justify-content-between align-items-center">

                <h4 className="card-title mb-0">
                  Active Restrictions
                </h4>

                <Badge color="warning">
                  {restrictionData.length} Active Rules
                </Badge>

              </CardHeader>

              <CardBody>

                {pageLoading ? (
                  <div className="text-center py-5">
                    <Spinner size="sm" />
                    <div className="mt-2 text-muted">
                      Loading restrictions...
                    </div>
                  </div>
                ) : (
                  <TableContainer
                    columns={
                      restrictionColumns
                    }
                    data={
                      restrictionData
                    }
                    customPageSize={20}
                    isPagination={false}
                    className="text-nowrap"
                    theadClass="table-light"
                  />
                )}

              </CardBody>

            </Card>
          </Col>
        </Row>

        {/* =========================================
            BOTTOM SECTION
        ========================================= */}

        <Row>

          {/* =========================================
              ADD RESTRICTION
          ========================================= */}

          <Col lg={4}>
            <Card
              style={{
                minHeight: "500px",
              }}
            >

              <CardHeader>
                <h4 className="card-title mb-0">
                  Add Restriction
                </h4>
              </CardHeader>

              <CardBody>

                {/* ROOM TYPE */}

                <div className="mb-3">
                  <Label>
                    Room Type
                  </Label>

                  <Input
                    type="select"
                    name="roomTypeId"
                    value={
                      formData.roomTypeId
                    }
                    onChange={
                      handleChange
                    }
                  >
                    <option value="">
                      Select Room Type
                    </option>

                    {roomTypes.map(
                      (room) => (
                        <option
                          key={room._id}
                          value={room._id}
                        >
                          {room.name}
                        </option>
                      )
                    )}
                  </Input>
                </div>

                {/* START DATE */}

                <div className="mb-3">
                  <Label>
                    Start Date
                  </Label>

                  <Input
                    type="date"
                    name="startDate"
                    value={
                      formData.startDate
                    }
                    onChange={
                      handleChange
                    }
                  />
                </div>

                {/* END DATE */}

                <div className="mb-3">
                  <Label>
                    End Date
                  </Label>

                  <Input
                    type="date"
                    name="endDate"
                    min={
                      formData.startDate
                    }
                    value={
                      formData.endDate
                    }
                    onChange={
                      handleChange
                    }
                  />
                </div>

                {/* RESTRICTION TYPE */}

                <div className="mb-3">
                  <Label>
                    Restriction Type
                  </Label>

                  <Input
                    type="select"
                    name="restrictionType"
                    value={
                      formData.restrictionType
                    }
                    onChange={
                      handleRestrictionTypeChange
                    }
                  >
                    <option value="STOP_SELL">
                      Stop Sell
                    </option>

                    <option value="CTA">
                      Close To Arrival (CTA)
                    </option>

                    <option value="CTD">
                      Close To Departure (CTD)
                    </option>

                    <option value="MIN_STAY">
                      Minimum Stay
                    </option>

                    <option value="MAX_STAY">
                      Maximum Stay
                    </option>
                  </Input>
                </div>

                {/* VALUE */}

                <div className="mb-4">
                  <Label>
                    Value
                  </Label>

                  {[
                    "STOP_SELL",
                    "CTA",
                    "CTD",
                  ].includes(
                    formData.restrictionType
                  ) ? (
                    <Input
                      type="select"
                      name="value"
                      value={
                        formData.value
                      }
                      onChange={
                        handleChange
                      }
                    >
                      <option value="">
                        Select
                      </option>

                      <option value="true">
                        Enable
                      </option>

                      <option value="false">
                        Disable
                      </option>
                    </Input>
                  ) : (
                    <Input
                      type="number"
                      name="value"
                      value={
                        formData.value
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="Enter nights"
                      min="1"
                    />
                  )}
                </div>

                {/* APPLY */}

                <Button
                  color="danger"
                  className="w-100"
                  onClick={
                    handleSave
                  }
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner
                        size="sm"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bx bx-save me-1" />
                      Apply Restriction
                    </>
                  )}
                </Button>

              </CardBody>
            </Card>
          </Col>

          {/* =========================================
              HISTORY
          ========================================= */}

          <Col lg={8}>
            <Card
              style={{
                minHeight: "500px",
              }}
            >

              <CardHeader className="d-flex justify-content-between align-items-center">

                <h4 className="card-title mb-0">
                  Restriction History
                </h4>

                <Badge color="secondary">
                  Last 50 Updates
                </Badge>

              </CardHeader>

              <CardBody>

                <TableContainer
                  columns={
                    logColumns
                  }
                  data={
                    restrictionLogs
                  }
                  customPageSize={10}
                  isPagination={false}
                  className="text-nowrap"
                  theadClass="table-light"
                />

              </CardBody>

            </Card>
          </Col>

        </Row>

      </Container>
    </div>
  );
};

export default RestrictionsManagement;